import { useEffect, useState } from 'react'
import {
  getSubscription, isStandalone, permission, pushConfigured, pushSupported,
  subscribe, unsubscribe,
} from '../lib/push.js'

/**
 * Lives on the Reminders screen, where a reminder that cannot reach him is
 * the thing he would notice.
 *
 * Every refusal says the actual reason. "Notifications are off" when the real
 * problem is that the app is in a Safari tab is the kind of message that costs
 * an hour.
 */
export default function PushToggle({ toast }) {
  const [on, setOn] = useState(false)
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!pushSupported()) { setReady(true); return }
    getSubscription()
      .then((s) => setOn(Boolean(s)))
      .catch(() => setOn(false))
      .finally(() => setReady(true))
  }, [])

  if (!ready) return null

  if (!pushSupported()) {
    return (
      <p className="lbl" style={{ margin: '14px 2px 0' }}>
        This browser cannot do notifications.
      </p>
    )
  }

  if (!isStandalone()) {
    return (
      <div className="notice" style={{ marginTop: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-dim)' }}>
          Reminders can only reach you from the home-screen app. Add it with Share, then
          <b> Add to Home Screen</b>, and turn them on from there.
        </p>
      </div>
    )
  }

  if (!pushConfigured()) {
    return (
      <div className="notice" style={{ marginTop: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-dim)' }}>
          Notifications are not set up yet. The sender needs its keys before this can be
          switched on.
        </p>
      </div>
    )
  }

  if (permission() === 'denied') {
    return (
      <div className="notice" style={{ marginTop: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-dim)' }}>
          You turned notifications off for this app. iOS will not ask again — it has to be
          switched back on in Settings, Notifications, MyPersonalOS.
        </p>
      </div>
    )
  }

  return (
    <button
      type="button"
      className="btn ghost"
      style={{ marginTop: 14 }}
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          if (on) {
            await unsubscribe()
            setOn(false)
            toast('Notifications off')
          } else {
            const res = await subscribe()
            if (res.ok) {
              setOn(true)
              toast('Notifications on')
            } else if (res.reason === 'denied') {
              toast('You said no. Settings, Notifications, MyPersonalOS to change it.')
            } else {
              toast(res.message || 'Could not turn those on.')
            }
          }
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? 'Working…' : on ? 'Turn notifications off' : 'Turn on notifications'}
    </button>
  )
}
